import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
export const BillingRunStatusSchema = z.enum([
    'analyzed',
    'publishing',
    'published',
]);
export const BillingRunProgressSchema = z.object({
    id: z.string(),
    periodMonth: z.number(),
    periodYear: z.number(),
    status: BillingRunStatusSchema,
    subscriptionCount: z.number(),
    consumptionCount: z.number(),
    unresolvedCount: z.number(),
    publishedCount: z.number(),
    publishTotal: z.number(),
    publishedAt: z.string().nullable(),
});
export const BillingCenterBreakdownSchema = z.object({
    siteId: z.string().nullable(),
    organizationId: z.string().nullable(),
    billingCenterName: z.string(),
    companyName: z.string(),
    subscriptionCount: z.number(),
    consumptionCount: z.number(),
});
export const BillingRunDetailSchema = BillingRunProgressSchema.extend({
    centers: z.array(BillingCenterBreakdownSchema),
});
export const DetailRowSchema = z.record(z.union([z.string(), z.number()]));
export const BillingCenterPreviewSchema = z.object({
    billingCenterName: z.string(),
    mois: z.string(),
    subscriptions: z.array(DetailRowSchema),
    consumptions: z.array(DetailRowSchema),
    consumptionTotal: z.number(),
});
/** Result summary of an annex import (kept for the multipart slice). */
export const BillingRunSummarySchema = z.object({
    billingRunId: z.string(),
    subscriptionCount: z.number(),
    consumptionCount: z.number(),
    unresolvedCount: z.number(),
    totalMarginCents: z.number(),
    /** Previous unpublished runs of the period replaced by this import. */
    supersededCount: z.number(),
});
// --- Org/site-scoped billing reads (montants only) ---
export const OrgBillingPeriodSchema = z.object({
    month: z.number(),
    year: z.number(),
    subscriptionTotal: z.number(),
    consumptionTotal: z.number(),
    total: z.number(),
    lineCount: z.number(),
});
export const OrgBillingSummarySchema = z.object({
    month: z.number(),
    year: z.number(),
    hasData: z.boolean(),
    subscriptionTotal: z.number(),
    consumptionTotal: z.number(),
    total: z.number(),
    topConsumingLines: z.array(z.object({
        mobileLineId: z.string(),
        number: z.string(),
        name: z.string().nullable(),
        amount: z.number(),
    })),
});
export const OrgBillingLineSchema = z.object({
    mobileLineId: z.string(),
    number: z.string(),
    name: z.string().nullable(),
    subscriptionTotal: z.number(),
    consumptionTotal: z.number(),
    total: z.number(),
});
export const OrgBillingLinesSchema = z.object({
    month: z.number(),
    year: z.number(),
    hasData: z.boolean(),
    lines: z.array(OrgBillingLineSchema),
});
// --- Per-line billing (line detail tab) ---
export const ConsumptionUnitSchema = z.enum(['SECONDS', 'OCTETS', 'QTE']);
export const LineSubscriptionBillingRowSchema = z.object({
    type: z.enum(['ABONNEMENT', 'FRAIS_PONCTUEL']),
    label: z.string(),
    productCode: z.string(),
    period: z.string(),
    /** Billed amount in euros (sub-cent precision possible). */
    amount: z.number(),
});
export const LineConsumptionBillingRowSchema = z.object({
    date: z.string(),
    productFamily: z.string(),
    productLabel: z.string(),
    unit: ConsumptionUnitSchema,
    value: z.number(),
    amount: z.number(),
});
export const LineBillingPeriodSchema = z.object({
    month: z.number(),
    year: z.number(),
});
export const LineBillingDetailSchema = z.object({
    month: z.number(),
    year: z.number(),
    hasData: z.boolean(),
    subscription: z.array(LineSubscriptionBillingRowSchema),
    consumption: z.array(LineConsumptionBillingRowSchema),
    subscriptionTotal: z.number(),
    consumptionTotal: z.number(),
});
const orgBillingQuery = z.object({
    siteId: z.string().min(1).optional(),
    // Period optional: omitted → the scope's latest available period.
    month: z.coerce.number().int().min(1).max(12).optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
});
const runParams = z.object({ id: z.string().min(1) });
/** Multipart fields of the annex import — everything is a string on the wire. */
export const ImportBillingRunBodySchema = z.object({
    billedMonth: z.coerce.number().int().min(1).max(12),
    billedYear: z.coerce.number().int().min(2000).max(2100),
});
/** Billing reads, run pilotage and the multipart annex import. */
export const billingContract = c.router({
    /**
     * ① Analyse — import the annexes. The client sends FormData: the
     * `subscriptionFile`/`consumptionFile` entries (repeatable) plus the
     * fields below; files are handled by the server interceptor, not the
     * schema.
     */
    importRun: {
        method: 'POST',
        path: '/admin/billing/runs',
        contentType: 'multipart/form-data',
        body: ImportBillingRunBodySchema,
        responses: { 201: BillingRunSummarySchema },
        summary: 'Import the Sewan annexes and analyse them',
    },
    getOrgBillingPeriods: {
        method: 'GET',
        path: '/organizations/:organizationId/billing/periods',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        query: orgBillingQuery,
        responses: { 200: z.array(OrgBillingPeriodSchema) },
        summary: 'Available billing periods of a scope',
    },
    getOrgBillingSummary: {
        method: 'GET',
        path: '/organizations/:organizationId/billing/summary',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        query: orgBillingQuery,
        responses: { 200: OrgBillingSummarySchema },
        summary: 'Billing summary of a scope for a period',
    },
    getOrgBillingLines: {
        method: 'GET',
        path: '/organizations/:organizationId/billing/lines',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        query: orgBillingQuery,
        responses: { 200: OrgBillingLinesSchema },
        summary: 'Per-line billing breakdown of a scope',
    },
    getLineBillingDetail: {
        method: 'GET',
        path: '/lines/:lineId/billing-detail',
        pathParams: z.object({ lineId: z.string().min(1) }),
        query: z.object({
            month: z.coerce.number().int().min(1).max(12).optional(),
            year: z.coerce.number().int().min(2000).max(2100).optional(),
        }),
        responses: { 200: LineBillingDetailSchema },
        summary: 'Billing of one line for one month',
    },
    getLineBillingPeriods: {
        method: 'GET',
        path: '/lines/:lineId/billing-periods',
        pathParams: z.object({ lineId: z.string().min(1) }),
        responses: { 200: z.array(LineBillingPeriodSchema) },
        summary: 'Billed periods available for a line',
    },
    getLatestActiveRun: {
        method: 'GET',
        path: '/admin/billing/runs/latest-active',
        responses: { 200: BillingRunProgressSchema.nullable() },
        summary: 'Most recent not-yet-published run',
    },
    getRun: {
        method: 'GET',
        path: '/admin/billing/runs/:id',
        pathParams: runParams,
        responses: { 200: BillingRunDetailSchema },
        summary: 'Run status + per-center breakdown',
    },
    getRunProgress: {
        method: 'GET',
        path: '/admin/billing/runs/:id/progress',
        pathParams: runParams,
        responses: { 200: BillingRunProgressSchema },
        summary: 'Lightweight run progress (polled during publishing)',
    },
    getCenterPreview: {
        method: 'GET',
        path: '/admin/billing/runs/:id/centers/:siteId/preview',
        pathParams: z.object({
            id: z.string().min(1),
            siteId: z.string().min(1),
        }),
        responses: { 200: BillingCenterPreviewSchema },
        summary: 'Web preview of one billing centre detail',
    },
    publishRun: {
        method: 'POST',
        path: '/admin/billing/runs/:id/publish',
        pathParams: runParams,
        body: c.noBody(),
        responses: { 201: z.object({ started: z.boolean() }) },
        summary: 'Trigger publication (fire-and-forget; poll the run)',
    },
    /** Only for a run not yet published (analyzed or stale publishing). */
    cancelRun: {
        method: 'DELETE',
        path: '/admin/billing/runs/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: z.undefined(),
        responses: { 200: z.object({ success: z.boolean() }) },
        summary: 'Cancel an unpublished billing run (run + lines + deposited files)',
    },
});
